/**
 * relativeTime — gemeinsame Zeit-Formatierung für nutzersichtbare Zeitstempel.
 *
 * Warum zentral: „vor 12 Tagen" allein ist mehrdeutig, ein reines Datum allein
 * beantwortet „ist das noch frisch?" nicht. Deshalb gibt es beides — und eine
 * kombinierte Form, die relativ UND absolut zeigt.
 *
 * Wortlaut folgt bewusst formatActivityTime in MonthlyPlanPage.jsx
 * ("Gerade eben", "vor 5 Min.", "vor 2 Std.", "vor 3 Tagen"), damit die App
 * überall gleich klingt.
 *
 * Alle Funktionen geben bei fehlendem/kaputtem Wert null zurück — der Aufrufer
 * lässt die Zeile dann weg, statt „Invalid Date" zu rendern.
 */

// Interner Parser: liefert Millisekunden oder null (nie NaN nach außen).
const _ms = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
};

/** "Gerade eben" | "vor 5 Min." | "vor 2 Std." | "vor 3 Tagen" — oder null. */
export function formatRelativeTime(iso) {
  const t = _ms(iso);
  if (t === null) return null;
  const diff = Date.now() - t;
  // Zukunft (Uhr-Versatz zwischen Server und Browser) nicht als "vor -1 Min." zeigen.
  if (diff < 60000) return "Gerade eben";
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return `vor ${days} Tag${days === 1 ? "" : "en"}`;
}

/** "15.07.2026, 14:34 Uhr" — oder null. */
export function formatAbsoluteTime(iso) {
  const t = _ms(iso);
  if (t === null) return null;
  try {
    return (
      new Date(t).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }) + " Uhr"
    );
  } catch {
    return null;
  }
}

/** "vor 2 Std. (15.07.2026, 14:34 Uhr)" — oder null. */
export function formatRelativeWithAbsolute(iso) {
  const rel = formatRelativeTime(iso);
  const abs = formatAbsoluteTime(iso);
  if (!rel && !abs) return null;
  if (!abs) return rel;
  if (!rel) return abs;
  return `${rel} (${abs})`;
}

/**
 * Sind zwei Zeitstempel für die Anzeige unterscheidbar?
 *
 * Die absolute Anzeige hat Minuten-Auflösung — zwei Werte, die weniger als eine
 * Minute auseinanderliegen, würden identisch gerendert. Zwei gleich aussehende
 * Zeilen nebeneinander sind Schein-Genauigkeit; deshalb erst ab einer Minute
 * Unterschied als „verschieden" behandeln.
 */
export function isDistinctTime(a, b, toleranceMs = 60000) {
  const ta = _ms(a);
  const tb = _ms(b);
  if (ta === null || tb === null) return false;
  return Math.abs(ta - tb) >= toleranceMs;
}
